import { useState } from 'react';
import AppModal from '../common/AppModal';

interface ReactivatePoolModalProps {
  open: boolean;
  poolName: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

// Unlike deactivation, reactivation takes no reason - a single confirm step.
const ReactivatePoolModal = ({
  open,
  poolName,
  onClose,
  onConfirm,
}: ReactivatePoolModalProps) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (submitting) return;
    setError(null);
    onClose();
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to set pool to active.'
      );
      setSubmitting(false);
    }
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      maxWidthClassName="max-w-[420px]"
    >
      <div className="mb-4 text-center text-xl font-extrabold leading-tight text-dark">
        Set “{poolName}” to active?
      </div>
      <div className="text-center text-sm font-medium text-light">
        This pool will return to your team&rsquo;s active list and can take new
        services, repairs, and route assignments again.
      </div>

      {error && (
        <div className="mt-4 rounded-xl border border-danger/20 bg-dangerLight px-3.5 py-2.5 text-sm text-danger">
          {error}
        </div>
      )}

      <div className="mt-6 flex gap-3">
        <button
          type="button"
          disabled={submitting}
          onClick={handleClose}
          className="h-12 flex-1 rounded-xl border-[1.5px] border-grey bg-white text-[15px] font-semibold text-dark hover:bg-grey4 disabled:cursor-not-allowed disabled:opacity-60"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={submitting}
          onClick={handleConfirm}
          className="h-12 flex-1 rounded-xl bg-primary text-[15px] font-semibold text-white hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-45"
        >
          {submitting ? 'Setting Active…' : 'Set Active'}
        </button>
      </div>
      <div className="mt-3.5 text-center text-[12.5px] text-grey10">
        No reason required to reactivate.
      </div>
    </AppModal>
  );
};

export default ReactivatePoolModal;
